/**
 * System Layer Manager Service
 *
 * Single Source of Truth for all system layer operations.
 * Manages background, mockup image, and print area border layers.
 *
 * System layers are non-selectable, non-editable layers managed by the canvas.
 * They are filtered out during save/export operations.
 */

import { Canvas, Rect, type FabricObject } from 'fabric';
import { SYSTEM_LAYER_NAMES } from '@designdeck/shared';
import { logger } from '@/utils/logger';

// =============================================================================
// CONSTANTS - Re-exported from @designdeck/shared (Single Source of Truth)
// =============================================================================

// Stufe 4.2: SYSTEM_LAYER_NAMES is now defined in packages/shared
// to enable framework-independent usage (e.g. computeRelativeDesignStateFromJSON).
// Re-exported here for backwards compatibility with existing imports.
export {
  SYSTEM_LAYER_NAMES,
  SYSTEM_LAYER_NAME_SET,
  type SystemLayerName,
} from '@designdeck/shared';

// =============================================================================
// LAYER IDENTIFICATION
// =============================================================================

/**
 * Check if a layer name indicates a system layer.
 * System layers start with '__' prefix.
 */
export function isSystemLayer(name: string | undefined): boolean {
  if (!name) return false;
  return name.startsWith('__');
}

/**
 * Heuristic to identify system-like objects in legacy JSON data.
 * Used to filter out objects that were saved without proper `name` property.
 *
 * This handles legacy data where system layers were accidentally serialized
 * without the `name` property that would normally identify them.
 *
 * @param obj - JSON object from canvas serialization
 * @returns true if the object appears to be a system layer
 */
export function isSystemLikeObject(obj: Record<string, unknown>): boolean {
  const name = obj.name as string | undefined;
  const type = obj.type as string | undefined;

  // 1. Explicit system layer (has proper name)
  if (name?.startsWith('__')) return true;

  // CRITICAL: If object has a name that doesn't start with '__', it's a user object
  // This prevents user objects at (0,0) from being incorrectly filtered
  if (name && !name.startsWith('__')) {
    return false;
  }

  // 2. Heuristic: Full-canvas non-selectable Rect at (0,0) → Background
  // Only apply if object has NO name (legacy data)
  if (
    !name && // Only apply heuristic if no name exists
    type === 'Rect' &&
    obj.selectable === false &&
    obj.left === 0 &&
    obj.top === 0 &&
    obj.fill !== 'transparent' // Background has solid fill
  ) {
    return true;
  }

  // 3. Heuristic: Image at (0,0) non-selectable → Mockup
  if (
    !name && // Only apply heuristic if no name exists
    (type === 'Image' || type === 'image') &&
    obj.selectable === false &&
    obj.left === 0 &&
    obj.top === 0
  ) {
    return true;
  }

  // 4. Heuristic: Transparent Rect with dashed stroke → PrintBorder
  if (
    !name && // Only apply heuristic if no name exists
    type === 'Rect' &&
    obj.fill === 'transparent' &&
    Array.isArray(obj.strokeDashArray) &&
    obj.selectable === false
  ) {
    return true;
  }

  return false;
}

/**
 * Check if a JSON object represents a valid user object (not a system layer).
 * Inverse of isSystemLikeObject with explicit name check.
 *
 * @param obj - JSON object from canvas serialization
 * @returns true if the object is a user-created design element
 */
export function isUserObject(obj: Record<string, unknown>): boolean {
  return !isSystemLikeObject(obj);
}

/**
 * Get all system layer objects from the canvas.
 */
export function getSystemLayers(canvas: Canvas): FabricObject[] {
  return canvas.getObjects().filter((obj) => isSystemLayer(obj.name));
}

/**
 * Get all user objects from the canvas (non-system layers).
 */
export function getUserObjects(canvas: Canvas): FabricObject[] {
  return canvas.getObjects().filter((obj) => !isSystemLayer(obj.name));
}

/**
 * Check if canvas has any user objects (designs).
 */
export function hasUserObjects(canvas: Canvas): boolean {
  return getUserObjects(canvas).length > 0;
}

// =============================================================================
// LAYER REMOVAL
// =============================================================================

/**
 * Remove all user objects from canvas, keeping system layers intact.
 * Used during position switch to clear designs before loading new position.
 */
export function removeUserObjects(canvas: Canvas): void {
  const userObjects = getUserObjects(canvas);
  userObjects.forEach((obj) => canvas.remove(obj));
  logger.debug(
    'SystemLayerManager',
    `Removed ${userObjects.length} user objects`
  );
}

/**
 * Remove all system layers from canvas.
 * Used before reinitializing layers with new config.
 */
export function removeSystemLayers(canvas: Canvas): void {
  const systemLayers = getSystemLayers(canvas);
  systemLayers.forEach((obj) => canvas.remove(obj));
  logger.debug(
    'SystemLayerManager',
    `Removed ${systemLayers.length} system layers`
  );
}

/**
 * Remove every object carrying the given system layer name. Makes re-adding
 * a layer idempotent when an earlier initialization is still in flight.
 */
export function removeSystemLayer(canvas: Canvas, name: string): void {
  canvas
    .getObjects()
    .filter((obj) => obj.name === name)
    .forEach((obj) => canvas.remove(obj));
}

/**
 * Update background color without reinitializing all layers.
 */
export function updateBackgroundColor(canvas: Canvas, color: string): void {
  const bgRect = canvas
    .getObjects()
    .find((obj) => obj.name === SYSTEM_LAYER_NAMES.BACKGROUND) as
    | Rect
    | undefined;

  if (bgRect) {
    bgRect.set({ fill: color });
    canvas.requestRenderAll();
  }
}

/**
 * Get the print area ClipPath Rect for constraining user objects.
 */
export function createPrintAreaClipPath(printArea: {
  x: number;
  y: number;
  width: number;
  height: number;
}): Rect {
  return new Rect({
    left: printArea.x,
    top: printArea.y,
    width: printArea.width,
    height: printArea.height,
    absolutePositioned: true,
  });
}

/**
 * Apply clipPath to all user objects on the canvas.
 */
export function applyClipPathToUserObjects(
  canvas: Canvas,
  printArea: { x: number; y: number; width: number; height: number }
): void {
  const clipPath = createPrintAreaClipPath(printArea);
  const userObjects = getUserObjects(canvas);

  userObjects.forEach((obj) => {
    obj.clipPath = clipPath;
  });

  canvas.requestRenderAll();
}
